import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  ShieldCheck,
  Calendar,
  Lock,
  Mail,
  Phone,
  MapPin,
  Camera,
  Save,
  CheckCircle2,
  AlertCircle,
  LogOut,
  Trash2,
  Sparkles,
  KeyRound,
  ExternalLink,
  ChevronRight,
  Clock,
  Check,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  updateClientProfile,
  deleteClientAccount,
  sendClientPasswordReset,
  checkClientDeletionEligibility,
  clientLogout,
} from '../lib/clientAuthService';
import { auth } from '../lib/firebase';
import { PairUpLogo } from '../components/common/PairUpLogo';

export const MyAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { userProfile, currentUser, refreshProfile } = useAuth();

  const [activeTab, setActiveTab] = useState<'profile' | 'edit' | 'security'>('profile');

  // Edit form state
  const [fullName, setFullName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [city, setCity] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Deletion modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteEligibilityError, setDeleteEligibilityError] = useState<string | null>(null);

  // Password reset state
  const [isSendingReset, setIsSendingReset] = useState(false);

  // Populate data
  useEffect(() => {
    if (userProfile) {
      setFullName((userProfile as any).fullName || userProfile.legalName || userProfile.name || '');
      setDisplayName(userProfile.displayName || userProfile.name || '');
      setPhoneNumber((userProfile as any).phoneNumber || userProfile.phone || '');
      setCity(userProfile.city || '');
      setBio(userProfile.bio || '');
      setAvatarUrl(
        userProfile.avatarUrl ||
          'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=400&q=80'
      );
    }
  }, [userProfile]);

  const clientId =
    (userProfile as any)?.clientId ||
    localStorage.getItem('pairup_client_id') ||
    'CL-MEMBER';

  const userEmail = userProfile?.email || auth.currentUser?.email || 'client@pairup.today';

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setNotice(null);

    try {
      await updateClientProfile({
        fullName,
        displayName,
        phoneNumber,
        city,
        bio,
        avatarUrl,
      });

      await refreshProfile();
      setNotice({ message: 'Profile details successfully updated and saved to Firestore!', type: 'success' });
      setActiveTab('profile');
    } catch (err: any) {
      setNotice({ message: err.message || 'Failed to update profile.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordReset = async () => {
    setIsSendingReset(true);
    setNotice(null);
    try {
      await sendClientPasswordReset(userEmail);
      setNotice({
        message: `Password reset link sent to ${userEmail}. Please check your inbox.`,
        type: 'success',
      });
    } catch (err: any) {
      setNotice({ message: err.message || 'Could not send password reset email.', type: 'error' });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleCheckDeletion = async () => {
    if (!auth.currentUser) return;
    setDeleteEligibilityError(null);
    const check = await checkClientDeletionEligibility(auth.currentUser.uid);
    if (!check.eligible) {
      setDeleteEligibilityError(check.message || 'Cannot delete account with active bookings.');
    }
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteAccount = async () => {
    if (deleteConfirmationText.trim().toUpperCase() !== 'DELETE') {
      alert('Please type "DELETE" into the confirmation box.');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteClientAccount('Client requested account deletion');
      alert('Your account has been deleted.');
      navigate('/signup', { replace: true });
    } catch (err: any) {
      alert(err.message || 'Failed to delete account.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleLogout = async () => {
    await clientLogout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col selection:bg-pink-500 selection:text-white">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-2xs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2.5">
            <PairUpLogo className="w-9 h-9 shrink-0 drop-shadow-xs" />
            <div className="flex flex-col">
              <span className="text-xl font-black tracking-tight text-slate-900 leading-none">
                PairUp<span className="text-pink-600">Today</span>
              </span>
              <span className="text-[10px] font-bold text-slate-400">My Account</span>
            </div>
          </Link>

          <div className="flex items-center gap-2.5">
            <Link
              to="/home"
              className="px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 rounded-full hover:bg-slate-100 transition-colors"
            >
              Back to Home
            </Link>
            <Link
              to="/my-bookings"
              className="px-3.5 py-1.5 text-xs font-bold text-pink-600 bg-pink-50 rounded-full hover:bg-pink-100 transition-colors"
            >
              My Bookings
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6 w-full">
        {/* Notice Banner */}
        {notice && (
          <div
            className={`p-4 rounded-2xl text-xs font-bold flex items-center gap-2.5 shadow-xs ${
              notice.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : 'bg-rose-50 text-rose-800 border border-rose-200'
            }`}
          >
            {notice.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span>{notice.message}</span>
          </div>
        )}

        {/* Profile Header Card */}
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="relative w-20 h-20 rounded-3xl overflow-hidden border-3 border-pink-500 shadow-md shrink-0">
              <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  {displayName || 'Verified Client'}
                </h1>
                <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold text-[11px] border border-emerald-300">
                  Active Client
                </span>
              </div>

              {/* Permanent Client ID */}
              <div className="flex items-center gap-2 text-xs">
                <span className="font-bold text-slate-400">Client ID:</span>
                <span className="font-mono font-black text-pink-600 bg-pink-50 px-2 py-0.5 rounded-md border border-pink-200">
                  {clientId}
                </span>
              </div>

              <div className="flex items-center gap-3 text-xs text-slate-500 pt-0.5">
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  {userEmail}
                </span>
                {phoneNumber && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5 text-slate-400" />
                    +91 {phoneNumber}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleLogout}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-rose-600 hover:bg-rose-50 border border-slate-200 rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 text-xs font-bold gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('profile')}
            className={`pb-3 px-4 border-b-2 transition-all cursor-pointer ${
              activeTab === 'profile'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            Profile Information
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('edit')}
            className={`pb-3 px-4 border-b-2 transition-all cursor-pointer ${
              activeTab === 'edit'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            Edit Profile
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('security')}
            className={`pb-3 px-4 border-b-2 transition-all cursor-pointer ${
              activeTab === 'security'
                ? 'border-pink-600 text-pink-600'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            Security & Controls
          </button>
        </div>

        {/* TAB 1: PROFILE INFORMATION */}
        {activeTab === 'profile' && (
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-black text-slate-900">Personal Information</h2>
                <p className="text-xs text-slate-500">
                  Your registered profile details linked to Firebase UID: <span className="font-mono text-slate-700">{auth.currentUser?.uid}</span>
                </p>
              </div>

              <button
                type="button"
                onClick={() => setActiveTab('edit')}
                className="px-3.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                Edit Information
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-2 text-xs">
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <span className="text-[11px] font-bold text-slate-400 block uppercase">Permanent Client ID</span>
                <span className="font-mono font-black text-pink-600 text-sm block">{clientId}</span>
                <span className="text-[10px] text-slate-400">Non-transferable internal client identifier</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <span className="text-[11px] font-bold text-slate-400 block uppercase">Full Legal Name</span>
                <span className="font-bold text-slate-900 text-sm block">{fullName || displayName}</span>
                <span className="text-[10px] text-slate-400">Protected & confidential</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <span className="text-[11px] font-bold text-slate-400 block uppercase">Display Name</span>
                <span className="font-bold text-slate-900 text-sm block">{displayName}</span>
                <span className="text-[10px] text-slate-400">Visible to companions</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <span className="text-[11px] font-bold text-slate-400 block uppercase">Verified Email</span>
                <span className="font-bold text-slate-900 text-sm block">{userEmail}</span>
                <span className="text-[10px] text-emerald-600 font-semibold">✓ Linked to Firebase Auth</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <span className="text-[11px] font-bold text-slate-400 block uppercase">Contact Phone</span>
                <span className="font-bold text-slate-900 text-sm block">+91 {phoneNumber || 'Not provided'}</span>
                <span className="text-[10px] text-slate-400">Used for booking SMS alerts</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <span className="text-[11px] font-bold text-slate-400 block uppercase">Primary City</span>
                <span className="font-bold text-slate-900 text-sm block">{city || 'Kolkata, West Bengal'}</span>
                <span className="text-[10px] text-slate-400">Used for nearby companion radius</span>
              </div>
            </div>

            {bio && (
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1">
                <span className="text-[11px] font-bold text-slate-400 block uppercase">About Me / Meeting Notes</span>
                <p className="text-xs text-slate-700 leading-relaxed">{bio}</p>
              </div>
            )}

            {/* Quick Links Card */}
            <div className="p-4 rounded-2xl bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <strong className="text-xs font-black text-slate-900 block">Manage Your Companion Bookings</strong>
                <p className="text-[11px] text-slate-600">
                  Track upcoming meetups, view status updates, and access your Meeting Completion OTP codes.
                </p>
              </div>

              <Link
                to="/my-bookings"
                className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors shrink-0 flex items-center gap-1.5"
              >
                <span>Go to My Bookings</span>
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}

        {/* TAB 2: EDIT PROFILE */}
        {activeTab === 'edit' && (
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
            <div>
              <h2 className="text-base font-black text-slate-900">Edit Permitted Profile Details</h2>
              <p className="text-xs text-slate-500">
                You can update your personal display info, phone number, and location. Protected fields like Client ID and user roles cannot be modified.
              </p>
            </div>

            <form onSubmit={handleSaveProfile} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">Full Legal Name</label>
                  <input
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">Display Name (Public)</label>
                  <input
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">Mobile Phone Number</label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-800 mb-1">Primary City</label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">Avatar Photo URL</label>
                <input
                  type="url"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">About Me / Meeting Notes</label>
                <textarea
                  rows={3}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white resize-none"
                />
              </div>

              <div className="pt-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab('profile')}
                  className="px-4 py-2 bg-slate-100 text-slate-700 text-xs font-bold rounded-xl hover:bg-slate-200 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-2 bg-pink-600 hover:bg-pink-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>{isSaving ? 'Saving...' : 'Save Profile Changes'}</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* TAB 3: SECURITY & DATA CONTROLS */}
        {activeTab === 'security' && (
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
            <div>
              <h2 className="text-base font-black text-slate-900">Security & Account Controls</h2>
              <p className="text-xs text-slate-500">
                Manage your credentials, password reset, and account lifecycle.
              </p>
            </div>

            {/* Password Reset Section */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <strong className="text-xs font-bold text-slate-900 block">Password Reset</strong>
                <p className="text-[11px] text-slate-500">
                  Send a password reset link to your registered email address ({userEmail}).
                </p>
              </div>

              <button
                type="button"
                onClick={handlePasswordReset}
                disabled={isSendingReset}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl shadow-xs transition-colors shrink-0 cursor-pointer disabled:opacity-50"
              >
                {isSendingReset ? 'Sending Email...' : 'Send Password Reset'}
              </button>
            </div>

            {/* Account Deletion Section */}
            <div className="p-4 rounded-2xl bg-rose-50/70 border border-rose-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <strong className="text-xs font-bold text-rose-950 block">Delete Account</strong>
                <p className="text-[11px] text-rose-700 leading-relaxed">
                  Permanently delete your PairUpToday client profile and all associated data. This action is irreversible. You cannot delete an account with active or pending bookings.
                </p>
              </div>

              <button
                type="button"
                onClick={handleCheckDeletion}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors shrink-0 flex items-center gap-1.5 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete Account</span>
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Account Deletion Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-slate-200 max-w-md w-full p-6 space-y-4 shadow-2xl">
            <div className="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <Trash2 className="w-6 h-6" />
            </div>

            <div className="text-center space-y-1">
              <h3 className="text-lg font-black text-slate-900">Confirm Account Deletion</h3>
              <p className="text-xs text-slate-500">
                Are you sure you want to permanently delete your Client account ({clientId})?
              </p>
            </div>

            {deleteEligibilityError ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs font-bold text-amber-800">
                {deleteEligibilityError}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-200">
                  Please type <strong className="text-rose-600 font-mono">DELETE</strong> in the box below to confirm:
                </p>
                <input
                  type="text"
                  placeholder="DELETE"
                  value={deleteConfirmationText}
                  onChange={(e) => setDeleteConfirmationText(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-mono font-bold text-center uppercase"
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-xl transition-colors cursor-pointer"
              >
                Cancel
              </button>
              {!deleteEligibilityError && (
                <button
                  type="button"
                  disabled={isDeleting || deleteConfirmationText.trim().toUpperCase() !== 'DELETE'}
                  onClick={handleConfirmDeleteAccount}
                  className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors cursor-pointer disabled:opacity-40"
                >
                  {isDeleting ? 'Deleting...' : 'Confirm Deletion'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-slate-400 border-t border-slate-200 mt-10 bg-white">
        PairUpToday Client Portal • Zero-Membership Policy Strictly Enforced
      </footer>
    </div>
  );
};

export default MyAccountPage;
